import { request } from '@bedrock-rdk/utils';
import _uniq from 'lodash/uniq';
import { Parser } from '@bedrock/ad-engine';
// NOTE: it's not properly exported from the ad-engine
import { replaceURIMacros } from '@bedrock/ad-engine/lib/utils/macros.utils';
import { getScreenSize, SCREEN_WIDTH, SCREEN_HEIGHT } from '../../../modules/tornado/utils/sizes';
import { track } from '../../../modules/player/pause-ads/pauseAds.utils';

const VALID_IMAGE_TYPES = ['image/png', 'image/jpeg'];
const MAX_WIDTH = 3840;
const MAX_HEIGHT = 2160;
const LINEARITY_MISMATCH_ERROR_CODE = 201;
const OVERSIZED_RENDITION_ERROR_CODE = 501;
const UNSUPPORTED_RENDITION_ERROR_CODE = 503;
const ERROR_MESSAGES = {
  [LINEARITY_MISMATCH_ERROR_CODE]: 'only linear creatives found',
  [OVERSIZED_RENDITION_ERROR_CODE]: 'every supported rendition is bigger than 4K',
  [UNSUPPORTED_RENDITION_ERROR_CODE]: 'no supported rendition found',
};

const STATIC_ASSET_TYPE = 'static';
const IMPRESSION_EVENT = 'impression';
const VIEWABLE_EVENT = 'viewable';
const CREATIVE_VIEW_EVENT = 'creativeView';
const CLOSE_EVENT = 'close';
const ERROR_EVENT = 'error';

const isValidImage = asset =>
  asset.type === STATIC_ASSET_TYPE &&
  VALID_IMAGE_TYPES.includes(asset.creativetype?.toLowerCase()) &&
  Boolean(asset.src?.trim());

/** The parser only inherits numeric dimensions, anything else(e.g. "100%") is left as is */
const getDimension = value => (Number.isFinite(value) ? value : null);

/** A rendition of unknown dimensions cannot be judged oversized */
const isWithinDisplayableCap = ({ width, height }) =>
  (getDimension(width) === null || width <= MAX_WIDTH) && (getDimension(height) === null || height <= MAX_HEIGHT);

const getDistance = (asset, screenWidth, screenHeight) => {
  const width = getDimension(asset.width);
  const height = getDimension(asset.height);

  return width === null || height === null
    ? Number.POSITIVE_INFINITY
    : (width - screenWidth) ** 2 + (height - screenHeight) ** 2;
};

const getTrackers = (tracking, event) =>
  (tracking?.[event] || [])
    .filter(Boolean)
    .map(value => value.trim())
    .filter(Boolean);

/** Every error code has to be tracked on its own, as the VAST allows a single code per beacon */
const getErrorBeacons = (trackers, codes) =>
  (codes || []).flatMap(code => trackers.map(value => value.replace(/\[ERRORCODE\]/g, code)));

/**
 * Flattens the ads of a slot, a wrapper ad owns no creative but the VAST retrieved
 * from its VASTAdTagURI, whose ads take the place of their wrapper. The parser keeps the ads
 * of a sequence in their order and moves the ones without any sequence to the buffet,
 * the buffet ads are only meant to be used when none of the sequential ones can be displayed.
 *
 * @param {?Object} slot a parsed slot
 * @returns {{sequence: Object[], buffet: Object[]}} the ads of the slot and the ones of every VAST behind them
 */
const getAds = slot => {
  const sequence = [];
  const buffet = [];

  (slot?.ads || []).forEach(ad => {
    const wrapped = getAds(ad.wrapperVAST);

    sequence.push(ad, ...wrapped.sequence);
    buffet.push(...wrapped.buffet);
  });
  (slot?.buffet || []).forEach(ad => {
    const wrapped = getAds(ad.wrapperVAST);

    buffet.push(ad, ...wrapped.sequence, ...wrapped.buffet);
  });

  return { sequence, buffet };
};

/**
 * Collects the parsed slot and the ones retrieved from the wrappers it contains
 *
 * @param {?Object} slot the parsed slot
 * @returns {Object[]} every slot of the VAST chain
 */
const getSlots = slot =>
  slot
    ? [slot, ...[...(slot.ads || []), ...(slot.buffet || [])].flatMap(({ wrapperVAST }) => getSlots(wrapperVAST))]
    : [];

/**
 * Collects the renditions of an ad, they may be spread across several non-linear creatives
 *
 * @param {Object} ad a parsed ad
 * @returns {{creative: Object, asset: Object}[]} every rendition and the creative owning it
 */
const getRenditions = ad =>
  (ad.creatives || [])
    .filter(({ linear }) => !linear)
    .flatMap(creative => (creative.assets || []).map(asset => ({ creative, asset })));

/**
 * Tells the renditions too big to be displayed from the unsupported ones, and the ads only owning
 * linear creatives from the empty ones. The parser drops the creatives of the ads it has flagged,
 * their error stack is then the only witness left.
 *
 * @param {Object[]} ads flattened ads of the parsed slot, see getAds
 * @returns {?number} the VAST error code to report, null when no creative was available at all
 */
const getFailureCode = ads => {
  if (
    ads.some(
      ad =>
        (ad.errorStack || []).includes(OVERSIZED_RENDITION_ERROR_CODE) ||
        getRenditions(ad).some(({ asset }) => isValidImage(asset) && !isWithinDisplayableCap(asset)),
    )
  ) {
    return OVERSIZED_RENDITION_ERROR_CODE;
  }

  if (
    ads.some(ad => (ad.errorStack || []).includes(UNSUPPORTED_RENDITION_ERROR_CODE) || getRenditions(ad).length > 0)
  ) {
    return UNSUPPORTED_RENDITION_ERROR_CODE;
  }

  return ads.some(ad => (ad.creatives || []).some(({ linear, assets }) => linear && assets?.length))
    ? LINEARITY_MISMATCH_ERROR_CODE
    : null;
};

/**
 * Picks the rendition which is the closest to the given screen size, ignoring the ones bigger than 4K
 * and the ones which are not an image of a supported type.
 * Renditions with unknown dimensions are only used when no sized rendition matches.
 *
 * @param {Object[]} assets non-linear assets of a creative, one per rendition
 * @param {number} screenWidth current screen width
 * @param {number} screenHeight current screen height
 * @returns {?Object} the selected asset
 */
export const selectStaticResource = (assets, screenWidth, screenHeight) =>
  assets
    .filter(asset => isValidImage(asset) && isWithinDisplayableCap(asset))
    .map(asset => ({ asset, distance: getDistance(asset, screenWidth, screenHeight) }))
    .reduce((best, current) => (best && best.distance <= current.distance ? best : current), null)?.asset || null;

/**
 * Picks the best rendition of every ad which owns a suitable one, the following ones are fallbacks
 * for the case the previous ones cannot be loaded. An ad may spread its renditions
 * across several non-linear creatives, all of them are candidates.
 *
 * @param {Object[]} ads flattened ads of the parsed slot, see getAds
 * @param {number} screenWidth current screen width
 * @param {number} screenHeight current screen height
 * @returns {{ad: Object, creative: Object, resource: Object}[]} the selected ads, their creative and their rendition
 */
export const selectAds = (ads, screenWidth, screenHeight) =>
  ads
    .map(ad => {
      const renditions = getRenditions(ad);
      const resource = selectStaticResource(
        renditions.map(({ asset }) => asset),
        screenWidth,
        screenHeight,
      );
      const selection = renditions.find(({ asset }) => asset === resource);

      return selection && { ad, creative: selection.creative, resource };
    })
    .filter(Boolean);

export const fetchPauseAd = data => {
  const { host, targeting, brParams } = data || {};

  return new Promise((resolve, reject) => {
    const url = new URL(host);
    Object.entries(targeting).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });

    const screenSize = getScreenSize();
    const w = SCREEN_WIDTH[screenSize];
    const h = SCREEN_HEIGHT[screenSize];

    request(replaceURIMacros(url.toString()))
      .then(text => Parser.parse(text))
      .then(data => {
        const [parsed] = data?.slots || [];
        // both the wrapper ads and the VASTs they point to are taken into account
        const slots = getSlots(parsed);
        const { sequence, buffet } = getAds(parsed);
        const ads = [...sequence, ...buffet];
        const selections = selectAds(ads, w, h);
        // each slot carries the VAST level error trackers, each ad carries its own ones
        const slotError = _uniq(slots.flatMap(({ tracking }) => getTrackers(tracking, ERROR_EVENT)));
        const getErrorTrackers = adsToReport =>
          _uniq([...slotError, ...adsToReport.flatMap(ad => getTrackers(ad.tracking, ERROR_EVENT))]);
        // the parser stacks the codes of the issues it has met, each of them has to be reported
        const parsingErrors = _uniq([
          ...slots.flatMap(slot => getErrorBeacons(getTrackers(slot.tracking, ERROR_EVENT), slot.errorStack)),
          ...ads.flatMap(ad => getErrorBeacons(getTrackers(ad.tracking, ERROR_EVENT), ad.errorStack)),
        ]);

        if (!selections.length) {
          const code = getFailureCode(ads);

          track(_uniq([...parsingErrors, ...getErrorBeacons(getErrorTrackers(ads), code ? [code] : [])]));

          throw new Error(code ? `VAST error ${code}: ${ERROR_MESSAGES[code]}` : 'VAST error: no ad found');
        }

        track(parsingErrors);

        resolve({
          creatives: selections.map(({ ad, creative, resource }) => ({
            error: getErrorTrackers([ad]),
            image: resource.src.trim(),
            impression: [
              ...getTrackers(ad.tracking, IMPRESSION_EVENT),
              ...getTrackers(creative.tracking, CREATIVE_VIEW_EVENT),
            ],
            viewable: {
              beacons: getTrackers(ad.tracking, VIEWABLE_EVENT),
              delay: brParams?.viewableImpressionDelay,
            },
            close: _uniq([...getTrackers(ad.tracking, CLOSE_EVENT), ...getTrackers(creative.tracking, CLOSE_EVENT)]),
          })),
        });
      })
      .catch(error => reject(error));
  });
};
